import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { css, cx } from "styled-system/css";
import { Pencil, Trash2, Check } from "lucide-react";
import { api, type ApiResponse } from "~/lib/api";
import { ak, Modal, PageHead, Section, Tag } from "~/components/editorial";

// Default swatch for new labels (the label's own data, not page chrome).
const DEFAULT_COLOR = "#6366f1";

interface Label {
  id: string;
  name: string;
  color: string | null;
  _count: { tools: number };
}

export function LabelsPage() {
  const queryClient = useQueryClient();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editColor, setEditColor] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState(DEFAULT_COLOR);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["labels"],
    queryFn: () => api.get<ApiResponse<Label[]>>("/labels"),
  });
  const labels = data?.data ?? [];

  const startEdit = (label: Label) => {
    setEditingId(label.id);
    setEditName(label.name);
    setEditColor(label.color ?? DEFAULT_COLOR);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName("");
    setEditColor("");
  };

  const saveEdit = async () => {
    if (!editingId || !editName.trim()) return;
    setIsSubmitting(true);
    try {
      await api.patch(`/labels/${editingId}`, {
        name: editName.trim(),
        color: editColor || null,
      });
      queryClient.invalidateQueries({ queryKey: ["labels"] });
      refetch();
      cancelEdit();
    } catch (err) {
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`Delete label "${name}"? It will be removed from all tools.`))
      return;
    await api.delete(`/labels/${id}`);
    queryClient.invalidateQueries({ queryKey: ["labels"] });
    refetch();
  };

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setIsSubmitting(true);
    try {
      await api.post("/labels", {
        name: newName.trim(),
        color: newColor || null,
      });
      queryClient.invalidateQueries({ queryKey: ["labels"] });
      refetch();
      setNewName("");
      setNewColor(DEFAULT_COLOR);
      setShowAdd(false);
    } catch (err) {
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className={ak.page}>
        <div className={ak.loading}>Loading labels…</div>
      </div>
    );
  }

  const unused = labels.filter((l) => l._count.tools === 0).length;

  return (
    <div className={ak.page}>
      <PageHead
        back={{ to: "/tools", label: "Tools" }}
        kicker="Admin · Tools tracker"
        title="Labels"
        dek={
          labels.length === 0 ? (
            "Labels group tools by what they are for."
          ) : (
            <>
              <span className={ak.mono}>{labels.length}</span>{" "}
              {labels.length === 1 ? "label" : "labels"} in use across the tools
              tracker
              {unused > 0 && (
                <>
                  ; <span className={ak.mono}>{unused}</span> tag no tools
                </>
              )}
              .
            </>
          )
        }
        actions={
          <button onClick={() => setShowAdd(true)} className={ak.btn}>
            Add label
          </button>
        }
      />

      <Section kicker="All labels">
        <div className={ak.tableWrap}>
          <table className={ak.table}>
            <thead>
              <tr>
                <th>Label</th>
                <th>Color</th>
                <th data-num>Tools</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {labels.length === 0 && (
                <tr>
                  <td colSpan={4} className={ak.muted}>
                    No labels yet.
                  </td>
                </tr>
              )}
              {labels.map((label) =>
                editingId === label.id ? (
                  <tr key={label.id}>
                    <td>
                      <input
                        type="text"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        className={cx(ak.input, styles.inlineInput)}
                        aria-label="Label name"
                        autoFocus
                      />
                    </td>
                    <td>
                      <ColorField value={editColor} onChange={setEditColor} />
                    </td>
                    <td data-num>{label._count.tools}</td>
                    <td>
                      <div className={cx(ak.actions, styles.right)}>
                        <button
                          onClick={saveEdit}
                          disabled={isSubmitting}
                          className={cx(ak.btn, ak.btnSm)}
                        >
                          <Check size={12} />
                          Save
                        </button>
                        <button
                          onClick={cancelEdit}
                          className={cx(ak.btnSecondary, ak.btnSm)}
                        >
                          Cancel
                        </button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  <tr key={label.id}>
                    <td>
                      <Tag name={label.name} />
                    </td>
                    <td>
                      <span className={styles.colorCell}>
                        <span
                          className={styles.swatch}
                          style={{
                            backgroundColor: label.color ?? DEFAULT_COLOR,
                          }}
                          aria-hidden
                        />
                        <span className={cx(ak.mono, ak.muted)}>
                          {(label.color ?? DEFAULT_COLOR).toLowerCase()}
                        </span>
                      </span>
                    </td>
                    <td data-num>{label._count.tools}</td>
                    <td>
                      <div className={cx(ak.actions, styles.right)}>
                        <button
                          onClick={() => startEdit(label)}
                          className={ak.iconBtn}
                          title="Edit"
                          aria-label={`Edit ${label.name}`}
                        >
                          <Pencil size={13} />
                        </button>
                        <button
                          onClick={() => handleDelete(label.id, label.name)}
                          className={cx(ak.iconBtn, ak.iconBtnDanger)}
                          title="Delete"
                          aria-label={`Delete ${label.name}`}
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ),
              )}
            </tbody>
          </table>
        </div>
        <p className={ak.note}>
          The Tools column counts each tool tagged with the label. Color shows
          only as a small swatch beside the label; deleting a label removes it
          from every tool.
        </p>
      </Section>

      {showAdd && (
        <Modal title="Add a label" onClose={() => setShowAdd(false)}>
          <form onSubmit={handleAdd}>
            <div className={ak.field}>
              <label className={ak.label} htmlFor="label-name">
                Name *
              </label>
              <input
                id="label-name"
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Design, Backend…"
                className={ak.input}
                autoFocus
                required
              />
            </div>
            <div className={ak.field}>
              <label className={ak.label}>Color</label>
              <div className={styles.colorRow}>
                <ColorField value={newColor} onChange={setNewColor} />
                <Tag name={newName || "Preview"} color={newColor} />
              </div>
            </div>
            <div className={ak.modalActions}>
              <button
                type="button"
                onClick={() => setShowAdd(false)}
                className={ak.btnSecondary}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!newName.trim() || isSubmitting}
                className={ak.btn}
              >
                {isSubmitting ? "Creating…" : "Create"}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

function ColorField({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <span className={styles.colorCell}>
      <input
        type="color"
        value={value || DEFAULT_COLOR}
        onChange={(e) => onChange(e.target.value)}
        className={styles.colorInput}
        aria-label="Label color"
      />
      <span className={cx(ak.mono, ak.muted)}>
        {(value || DEFAULT_COLOR).toLowerCase()}
      </span>
    </span>
  );
}

const styles = {
  inlineInput: css({ py: "4px!", fontSize: "13px!", maxWidth: "240px" }),
  right: css({ justifyContent: "flex-end" }),
  colorCell: css({
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "12px",
  }),
  swatch: css({ display: "inline-block", width: "12px", height: "12px" }),
  colorInput: css({
    width: "28px",
    height: "24px",
    p: "1px",
    border: "1px solid",
    borderColor: "ed.rule",
    borderRadius: "0",
    bg: "ed.paper",
    cursor: "pointer",
  }),
  colorRow: css({ display: "flex", alignItems: "center", gap: "12px" }),
};
